import { Error } from "@components/Error";
import { ILSItemPlaceholder } from "@components/ILSPlaceholder/ILSPlaceholder";
import { InfoMessage } from "@components/InfoMessage";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Container, Header, Label } from "semantic-ui-react";
import LoansList from "../LoansList";
import LoansListEntry from "../LoansListEntry";

const LOAN_STATES = {
  CANCELLED: "CANCELLED",
};

const LABEL_COLORS = {
  SUCCESS: "green",
  ERROR: "red",
  WARNING: "orange",
};

const LABEL_SIZES = {
  TINY: "tiny",
  SMALL: "small",
};

const DEFAULTS = {
  INITIAL_PAGE: 1,
  ROWS_PER_PAGE: 5,
};

const MESSAGES = {
  NO_LOANS_TITLE: "No past loans",
  NO_LOANS_MESSAGE: "You do not have any past loan.",
  DUE_DATE_UNAVAILABLE: "Due date unavailable",
  OVERDUE: "OVERDUE",
  CANCELLED: "Cancelled",
  LOANED_ON: "Loaned on",
  DUE_DATE: "Due date",
  RETURNED_ON: "Returned on",
};

const SORT_CONFIG = "transaction_date:desc,start_date:desc";

const hasMetadata = (loan) => loan?.metadata;

const findReturnDate = (loan) =>
  hasMetadata(loan) ? loan.metadata.transaction_date : null;

const findDueDate = (loan) => {
  if (!hasMetadata(loan)) return null;

  const { metadata } = loan;
  const returnDate = findReturnDate(loan);

  // For returned/past loans, use due_date (original due date)
  // For active loans, use end_date (which may include extensions/overrides)
  if (returnDate) {
    // Loan has been returned - use original due date
    return (
      metadata.due_date ||
      metadata.end_date ||
      metadata.request_expire_date ||
      null
    );
  }

  // Active loan - use current end_date (includes updates/overrides)
  return (
    metadata.end_date ||
    metadata.due_date ||
    metadata.request_expire_date ||
    null
  );
};

const formatDate = (dateString) =>
  dateString ? DateTime.fromISO(dateString).toLocaleString() : "N/A";

const getSortDate = (loan) => {
  if (!hasMetadata(loan)) return "";

  const returnDate = findReturnDate(loan);
  if (returnDate) return returnDate;

  const { metadata } = loan;
  return metadata.start_date || metadata.created || "";
};

const sortLoansByMostRecent = (loans) => {
  if (!loans || !Array.isArray(loans)) return loans;

  return [...loans].sort((a, b) => {
    const dateA = getSortDate(a);
    const dateB = getSortDate(b);

    if (!dateA && !dateB) return 0;
    if (!dateA) return 1;
    if (!dateB) return -1;

    return (
      DateTime.fromISO(dateB).toMillis() - DateTime.fromISO(dateA).toMillis()
    );
  });
};

const isOverdue = (loan) => {
  if (!hasMetadata(loan)) return false;

  const { metadata } = loan;
  const returnDate = findReturnDate(loan);
  const dueDate = findDueDate(loan);

  // Check metadata flag first - this is set by the system
  if (metadata.is_overdue === true) return true;

  // If no due date available, can't determine overdue status
  if (!dueDate) return false;

  const dueDay = DateTime.fromISO(dueDate).startOf("day");

  // If there's a return date, compare it to due date
  if (returnDate) {
    const returnDay = DateTime.fromISO(returnDate).startOf("day");
    const wasOverdue = returnDay > dueDay;

    return wasOverdue;
  }

  // If no return date (still active), check if currently overdue
  const today = DateTime.now().startOf("day");
  return today > dueDay;
};

const LabelWrapper = ({ children }) => (
  <div className="pt-default">{children}</div>
);

LabelWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

const BasicLabel = ({ children, detail }) => (
  <Label basic>
    {children}
    {detail && <Label.Detail>{detail}</Label.Detail>}
  </Label>
);

BasicLabel.propTypes = {
  children: PropTypes.node.isRequired,
  detail: PropTypes.string,
};

const StatusLabel = ({ color, size, children }) => (
  <Label color={color} size={size}>
    {children}
  </Label>
);

StatusLabel.propTypes = {
  color: PropTypes.string.isRequired,
  size: PropTypes.string,
  children: PropTypes.node.isRequired,
};

class PastLoansListEntry extends Component {
  renderCancelledLabel = () => (
    <LabelWrapper>
      <Label basic>{MESSAGES.CANCELLED}</Label>
    </LabelWrapper>
  );

  renderActiveLabels = (startDate, loan) => {
    const dueDate = findDueDate(loan);
    const returnDate = findReturnDate(loan);
    const shouldShowOverdue = isOverdue(loan);

    const isDueDateUnavailable = !dueDate;

    return (
      <LabelWrapper>
        <BasicLabel detail={formatDate(startDate)}>
          {MESSAGES.LOANED_ON}
        </BasicLabel>

        {dueDate && (
          <BasicLabel detail={formatDate(dueDate)}>
            {MESSAGES.DUE_DATE}
          </BasicLabel>
        )}

        {isDueDateUnavailable && (
          <StatusLabel color={LABEL_COLORS.WARNING} size={LABEL_SIZES.TINY}>
            {MESSAGES.DUE_DATE_UNAVAILABLE}
          </StatusLabel>
        )}

        {returnDate && (
          <Label
            basic
            color={
              shouldShowOverdue ? LABEL_COLORS.ERROR : LABEL_COLORS.SUCCESS
            }
          >
            {MESSAGES.RETURNED_ON}
            <Label.Detail>{formatDate(returnDate)}</Label.Detail>
          </Label>
        )}

        {shouldShowOverdue && (
          <StatusLabel color={LABEL_COLORS.ERROR} size={LABEL_SIZES.SMALL}>
            {MESSAGES.OVERDUE}
          </StatusLabel>
        )}
      </LabelWrapper>
    );
  };

  renderLabels = (startDate, loan) => {
    const { metadata } = loan;

    return metadata.state === LOAN_STATES.CANCELLED
      ? this.renderCancelledLabel()
      : this.renderActiveLabels(startDate, loan);
  };

  render() {
    const { loan } = this.props;
    return (
      <LoansListEntry
        loan={loan}
        withLinking={false}
        extraItemProps={{
          itemMetaCmp: this.renderLabels(loan.metadata.start_date, loan),
        }}
      />
    );
  }
}

PastLoansListEntry.propTypes = {
  loan: PropTypes.object.isRequired,
};

export default class PatronPastLoans extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activePage: props.initialPage,
      refreshKey: 0,
    };
  }

  componentDidMount() {
    this.fetchPatronPastLoans();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.loans?.updated !== this.props.loans?.updated) {
      this.fetchPatronPastLoans();
    }
  }

  fetchPatronPastLoans = () => {
    const { activePage } = this.state;
    const { patronPid, fetchPatronPastLoans, rowsPerPage } = this.props;

    fetchPatronPastLoans(patronPid, {
      page: activePage,
      size: rowsPerPage,
      sort: SORT_CONFIG,
    });
  };

  forceRefresh = () => {
    this.setState(
      (prevState) => ({ refreshKey: prevState.refreshKey + 1 }),
      this.fetchPatronPastLoans
    );
  };

  handlePageChange = (newPage) => {
    this.setState({ activePage: newPage }, this.fetchPatronPastLoans);
  };

  getSortedLoans = () => {
    const { loans } = this.props;
    if (!loans?.hits) return loans;

    return {
      ...loans,
      hits: sortLoansByMostRecent(loans.hits),
    };
  };

  getHeaderTitle = (total) => `Your past loans (${total || 0})`;

  render() {
    const { error, isLoading, rowsPerPage } = this.props;
    const { activePage, refreshKey } = this.state;

    const sortedLoans = this.getSortedLoans();
    const headerTitle = this.getHeaderTitle(sortedLoans?.total);

    return (
      <Container className="spaced" key={refreshKey}>
        <Header
          as="h2"
          content={headerTitle}
          className="highlight"
          textAlign="center"
        />
        <ILSItemPlaceholder fluid isLoading={isLoading}>
          <Error error={error}>
            <LoansList
              activePage={activePage}
              isLoading={isLoading}
              loans={sortedLoans}
              onPageChange={this.handlePageChange}
              rowsPerPage={rowsPerPage}
              renderListEntry={(loan) => <PastLoansListEntry loan={loan} />}
              noLoansCmp={
                <InfoMessage
                  title={MESSAGES.NO_LOANS_TITLE}
                  message={MESSAGES.NO_LOANS_MESSAGE}
                />
              }
            />
          </Error>
        </ILSItemPlaceholder>
      </Container>
    );
  }
}

PatronPastLoans.propTypes = {
  patronPid: PropTypes.string.isRequired,
  initialPage: PropTypes.number,
  rowsPerPage: PropTypes.number,
  loans: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.object.isRequired,
  fetchPatronPastLoans: PropTypes.func.isRequired,
};

PatronPastLoans.defaultProps = {
  initialPage: DEFAULTS.INITIAL_PAGE,
  rowsPerPage: DEFAULTS.ROWS_PER_PAGE,
};
